import React, { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { showSnackbar, Table } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useConfirm } from "components/hooks/useConfirm.tsx";

import { DataFormat } from "utils";

import { AdminLanguage } from "view/admin/utils/admin.types";
import LanguageCreateEditDialog from "view/admin/components/dialogs/LanguageCreateEditDialog";

import { DeleteLanguageDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

interface AdminManuageLanguagesProps {
	languages: AdminLanguage[];
	loading: boolean;
	refetch: () => void;
}

const AdminManuageLanguages = ({ languages, loading, refetch } : AdminManuageLanguagesProps ) => {
	const confirm = useConfirm();
	const [ deleteLanguage ] = useMutation(DeleteLanguageDocument);

	const [ languageDialog, setLanguageDialog ] = useState<{ open: boolean, language: AdminLanguage | null }>({
		open: false,
		language: null,
	});

	const onDeleteLanguage = (id: number) => {
		deleteLanguage({
			variables: {
				id: id,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
			refetch();
		});
	};

	const isApplicationLanguage = (code: string | null | undefined) => {
		const standardCode = code?.toUpperCase();
		return standardCode === "EN" || standardCode === "NL" || standardCode === "DE" || standardCode === "VI";
	};

	return (
		<React.Fragment>
			<Table
				id="adminManageLanguages"
				disableFooter
				content={languages}
				contentLoading={loading}
				columns={[ {
					id: "description",
					title: "Description",
				}, {
					id: "code",
					title: "Code",
				}, {
					id: "isApplicationLanguage",
					title: "Application language",
					render: (row: AdminLanguage) => DataFormat.booleanAsText(row.isApplicationLanguage),
				}, {
					id: "linkedLanguage.code",
					title: "Linked language",
					render: (row: AdminLanguage) => row.linkedLanguage?.code ?? "",
				} ]}
				rowActions={[ {
					icon: <EditIcon />,
					onClick: (row: AdminLanguage) => setLanguageDialog({ open: true, language: row }),
					disabled: (row: AdminLanguage) => isApplicationLanguage(row.code),
				}, {
					icon: <DeleteIcon />,
					disabled: (row: AdminLanguage) => isApplicationLanguage(row.code),
					onClick: (row: AdminLanguage) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							onDeleteLanguage(row.id);
						});
					},
				} ]}
				actions={[ {
					title: "Add",
					onClick: () => setLanguageDialog({ open: true, language: null }),
					pinned: true,
				} ]} />

			<LanguageCreateEditDialog
				open={languageDialog.open}
				onClose={() => setLanguageDialog({ open: false, language: null })}
				language={languageDialog.language}
				languages={languages}
				refetch={() => refetch()} />
		</React.Fragment>
	);
};

export default AdminManuageLanguages;
